/**
 * Wallet profit & loss model, shared by the PnL section and the wallet header.
 *
 * Each spin costs the machine price, unless the wallet's real spin history has
 * been imported, in which case the actual amount paid is used (0 for free
 * packs). Each card is valued at what the instant buyback paid for it when
 * that's known. A card the imported history shows was kept is valued at its
 * insured value (the wallet still holds it). Without imported history we
 * can't see what happened, so cards are assumed sold back at buyback rate ×
 * insured value.
 */

export interface PnlMachine {
  code: string;
  name: string;
  shortName: string | null;
  category: string | null;
  price: number;
  buybackRate: number;
  thumbnailUrl: string | null;
  ev: number | null;
  status: "open" | "closed" | "unknown";
  inCatalog: boolean;
  spins: number;
}

export interface PnlEvent {
  id: number;
  timestamp: string;
  gachaCode: string | null;
  insuredValue: number | null;
  nftName: string;
  nftImage: string | null;
  nftGradingCompany: string | null;
  nftGrade: string | null;
  paid: number | null;
  buybackAmount: number | null;
  free: boolean;
  kept: boolean;
  refunded: boolean;
}

export interface SpinSyncStatus {
  status: "idle" | "running" | "complete" | "error";
  spinCount: number;
  pagesFetched: number;
  backfillComplete: boolean;
  lastSyncedAt: string | null;
  lastRequestedAt: string | null;
  lastError: string | null;
  canRefresh: boolean;
  nextRefreshAt: string | null;
  busy: boolean;
}

/** One priced spin. */
export interface Spin {
  i: number; // 1-based position among the spins being shown
  t: number; // epoch ms
  event: PnlEvent;
  machine: PnlMachine;
  cost: number; // what was paid (list price unless the real spin is known)
  cardValue: number; // insured value of the card
  buyback: number; // what the card returned: actual buyback, insured value if kept, else estimated buyback
  net: number; // buyback - cost
  cumNet: number;
  actual: boolean; // cost and buyback come from the imported spin record
  free: boolean;
  kept: boolean; // card was not sold back; valued at insured value
}

export type FreeFilter = "all" | "paid" | "free";

interface PriceOptions {
  /** Machine codes to include; every machine when omitted. */
  enabled?: Set<string>;
  /** Drop spins before this epoch ms. */
  since?: number;
  /** Only paid spins, only free packs, or both (needs imported spin history). */
  free?: FreeFilter;
}

/** Price every event that passes the filters, in chronological order. */
export function priceSpins(
  events: PnlEvent[],
  machines: PnlMachine[],
  { enabled, since = -Infinity, free = "all" }: PriceOptions = {}
): { spins: Spin[]; unpriced: number } {
  const machineByCode = new Map(machines.map((m) => [m.code, m]));
  const spins: Spin[] = [];
  let unpriced = 0;
  let cumNet = 0;

  for (const event of events) {
    const machine = event.gachaCode ? machineByCode.get(event.gachaCode) : undefined;
    if (!machine || machine.price <= 0) {
      unpriced++;
      continue;
    }
    if (enabled && !enabled.has(machine.code)) continue;
    const t = new Date(event.timestamp).getTime();
    if (t < since) continue;
    if (free === "paid" && event.free) continue;
    if (free === "free" && !event.free) continue;

    const actual = event.paid !== null;
    const cost = event.paid ?? machine.price;
    const cardValue = event.insuredValue ?? 0;
    const buyback = event.refunded
      ? 0
      : event.buybackAmount !== null
        ? event.buybackAmount
        : event.kept
          ? cardValue
          : cardValue * machine.buybackRate;
    const net = buyback - cost;
    cumNet += net;
    spins.push({
      i: spins.length + 1, t, event, machine, cost, cardValue, buyback, net, cumNet,
      actual, free: event.free, kept: event.kept,
    });
  }
  return { spins, unpriced };
}

export interface PnlSummary {
  net: number;
  spent: number;
  returned: number;
  roi: number;
  hitRate: number;
  avgNet: number;
  peak: number;
  drawdown: number;
  worstCold: number;
  best: Spin;
  worst: Spin;
  first: number;
  last: number;
  actualCount: number; // spins priced from the imported history
  freeCount: number;
  freeValue: number; // list price of the free spins
  keptCount: number;
}

/** Totals and highlights over a priced series; null when empty. */
export function summarizeSpins(spins: Spin[]): PnlSummary | null {
  const firstSpin = spins[0];
  const lastSpin = spins[spins.length - 1];
  if (!firstSpin || !lastSpin) return null;

  let spent = 0, returned = 0, hits = 0, peak = 0, drawdown = 0, cold = 0, worstCold = 0;
  let actualCount = 0, freeCount = 0, freeValue = 0, keptCount = 0;
  let best = firstSpin, worst = firstSpin;
  for (const s of spins) {
    spent += s.cost;
    returned += s.buyback;
    const hit = s.net >= 0;
    if (hit) hits++;
    cold = hit ? 0 : cold + 1;
    worstCold = Math.max(worstCold, cold);
    peak = Math.max(peak, s.cumNet);
    drawdown = Math.max(drawdown, peak - s.cumNet);
    if (s.net > best.net) best = s;
    if (s.net < worst.net) worst = s;
    if (s.actual) actualCount++;
    if (s.free) {
      freeCount++;
      freeValue += s.machine.price;
    }
    if (s.kept) keptCount++;
  }
  const net = returned - spent;
  return {
    net, spent, returned,
    roi: spent > 0 ? net / spent : 0,
    hitRate: hits / spins.length,
    avgNet: net / spins.length,
    peak, drawdown, worstCold, best, worst,
    first: firstSpin.t,
    last: lastSpin.t,
    actualCount, freeCount, freeValue, keptCount,
  };
}

export const machineLabel = (m: PnlMachine): string =>
  m.shortName ?? m.name.replace(/ Gacha( Pack)?$/, "");
